import { useTranslation } from "@ferrivia/i18n";
import type { LabAnalyteCode } from "@ferrivia/api-contract";

/* Analyte-level copy: the label a reading carries, and the plain-language
   explainer behind it.

   Both maps are written out per code rather than looped over LAB_ANALYTES
   because i18next-parser needs a literal key per `t()` call — it can't
   extract a template-interpolated one. The Finnish defaults are the
   catalog's own `description` text, moved here verbatim.

   Lives in @ferrivia/lab-ui's `labs` namespace rather than either app's,
   so the clinic console renders the same analyte under its English label
   while the client PWA renders it in Finnish, from one catalog.

   These explainers are static per analyte: every client tapping ferritin
   reads the same words, whatever their own value is. Nothing here is
   selected, ordered or worded on the basis of a measurement — see the client
   app's CLAUDE.md, "The software does not interpret". */
export interface AnalyteCopy {
  labelFor: (code: string) => string;
  explainerFor: (code: string) => string | null;
}

export function useAnalyteCopy(): AnalyteCopy {
  const { t } = useTranslation("labs");

  const labels: Record<LabAnalyteCode, string> = {
    ferritin: t("analytes.ferritin", "Ferritiini"),
    crp: t("analytes.crp", "Herkkä CRP"),
    hemoglobin: t("analytes.hemoglobin", "Hemoglobiini"),
    eryt: t("analytes.eryt", "Punasolujen määrä"),
    hkr: t("analytes.hkr", "Hematokriitti"),
    mcv: t("analytes.mcv", "Punasolujen keskitilavuus (MCV)"),
    mch: t("analytes.mch", "Punasolun keskimääräinen hemoglobiinimäärä (MCH)"),
    rdw: t("analytes.rdw", "Punasolujen kokojakauma (RDW)"),
    leuk: t("analytes.leuk", "Leukosyytit"),
    trom: t("analytes.trom", "Trombosyytit"),
  };

  const explainers: Record<LabAnalyteCode, string> = {
    ferritin: t(
      "explainers.ferritin",
      "Ferritiini on kehon tärkein raudan varastoproteiini. Verestä mitattu ferritiiniarvo kertoo, paljonko rautaa kehossa on varastoituneena.",
    ),
    crp: t(
      "explainers.crp",
      "Herkkä CRP (C-reaktiivinen proteiini) on maksan tuottama proteiini, jonka pitoisuus veressä nousee elimistön tulehdustilassa.",
    ),
    hemoglobin: t(
      "explainers.hemoglobin",
      "Hemoglobiini on punasolujen sisältämä happea kuljettava proteiini. Se kertoo veren kyvystä kuljettaa happea kudoksiin.",
    ),
    eryt: t(
      "explainers.eryt",
      "Punasolujen eli erytrosyyttien määrä kertoo, kuinka monta happea kuljettavaa punasolua verinäytteessä on.",
    ),
    hkr: t("explainers.hkr", "Hematokriitti kertoo, kuinka suuri osuus verestä on tilavuudeltaan punasoluja."),
    mcv: t(
      "explainers.mcv",
      "Punasolujen keskitilavuus (MCV) kertoo yksittäisen punasolun keskimääräisen koon.",
    ),
    mch: t("explainers.mch", "Kertoo, kuinka paljon hemoglobiinia yhdessä punasolussa keskimäärin on."),
    rdw: t(
      "explainers.rdw",
      "Punasolujen kokojakauma (RDW) kertoo, kuinka paljon punasolujen koko vaihtelee keskenään.",
    ),
    leuk: t("explainers.leuk", "Leukosyytit eli valkosolut ovat osa elimistön puolustusjärjestelmää."),
    trom: t("explainers.trom", "Trombosyytit eli verihiutaleet osallistuvat veren hyytymiseen."),
  };

  return {
    // An unknown code renders as itself rather than as a blank row — a
    // partner lab sending an analyte the catalog doesn't carry yet is a
    // content gap, not a reason to drop a measured value off the screen.
    labelFor: (code) => labels[code as LabAnalyteCode] ?? code,
    explainerFor: (code) => explainers[code as LabAnalyteCode] ?? null,
  };
}
